import type { Component, ComponentProps } from "solid-js";

/**
 * Theme-aware inline SVG illustrations for empty states and headers.
 *
 * Every illustration paints with `currentColor` + opacity layers only, so it
 * inherits the surrounding text colour and adapts to any theme automatically
 * (no hard-coded palette). They are decorative: callers wrap them in a labelled
 * region, so each SVG is `aria-hidden`. Strokes scale with the box; use a width
 * class on the element.
 */
type IllustrationProps = ComponentProps<"svg">;

function baseProps(props: IllustrationProps) {
  return {
    viewBox: "0 0 120 120",
    fill: "none",
    role: "presentation",
    "aria-hidden": "true",
    ...props,
  } satisfies IllustrationProps;
}

/** Calendar with a blooming check — "build the habit / today is a fresh start". */
export const SproutCalendarIllustration: Component<IllustrationProps> = (props) => (
  <svg {...baseProps(props)}>
    {/* soft halo */}
    <circle cx="60" cy="60" r="52" class="text-accent" fill="currentColor" opacity="0.06" />
    {/* calendar body */}
    <rect
      x="30"
      y="34"
      width="60"
      height="56"
      rx="10"
      stroke="currentColor"
      stroke-width="3"
      class="text-accent"
      opacity="0.85"
    />
    <line
      x1="30"
      y1="48"
      x2="90"
      y2="48"
      stroke="currentColor"
      stroke-width="3"
      class="text-accent"
      opacity="0.85"
    />
    <line
      x1="44"
      y1="28"
      x2="44"
      y2="40"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
    />
    <line
      x1="76"
      y1="28"
      x2="76"
      y2="40"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
    />
    {/* sprout */}
    <path
      d="M60 84V66"
      stroke="currentColor"
      stroke-width="3.5"
      stroke-linecap="round"
      class="text-celebrate"
    />
    <path
      d="M60 70c-7 0-12-4-12-10 7 0 12 4 12 10Z"
      fill="currentColor"
      class="text-celebrate"
      opacity="0.55"
    />
    <path
      d="M60 66c7-1 11-6 11-12-7 0-12 5-11 12Z"
      fill="currentColor"
      class="text-celebrate"
      opacity="0.8"
    />
  </svg>
);

/** Broken/unplugged state — error boundary fallback. Calm, not alarming. */
export const BrokenLinkIllustration: Component<IllustrationProps> = (props) => (
  <svg {...baseProps(props)}>
    <circle cx="60" cy="60" r="52" class="text-accent" fill="currentColor" opacity="0.06" />
    {/* two halves of a link, pulled apart */}
    <path
      d="M52 44l-9 9a13 13 0 0 0 0 18 13 13 0 0 0 18 0l4-4"
      stroke="currentColor"
      stroke-width="4"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.85"
    />
    <path
      d="M68 76l9-9a13 13 0 0 0 0-18 13 13 0 0 0-18 0l-4 4"
      stroke="currentColor"
      stroke-width="4"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.85"
    />
    {/* spark gap */}
    <line
      x1="58"
      y1="50"
      x2="54"
      y2="46"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-celebrate"
    />
    <line
      x1="66"
      y1="62"
      x2="70"
      y2="66"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-celebrate"
    />
    <line
      x1="60"
      y1="56"
      x2="60"
      y2="56"
      stroke="currentColor"
      stroke-width="5"
      stroke-linecap="round"
      class="text-celebrate"
    />
  </svg>
);

/** Bar chart with a rising trend — analytics / reports. */
export const BarChartIllustration: Component<IllustrationProps> = (props) => (
  <svg {...baseProps(props)}>
    <circle cx="60" cy="60" r="52" class="text-accent" fill="currentColor" opacity="0.06" />
    {/* baseline */}
    <line
      x1="34"
      y1="86"
      x2="90"
      y2="86"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.5"
    />
    {/* bars */}
    <rect
      x="38"
      y="66"
      width="9"
      height="20"
      rx="2.5"
      fill="currentColor"
      class="text-accent"
      opacity="0.55"
    />
    <rect
      x="52"
      y="54"
      width="9"
      height="32"
      rx="2.5"
      fill="currentColor"
      class="text-accent"
      opacity="0.75"
    />
    <rect
      x="66"
      y="44"
      width="9"
      height="42"
      rx="2.5"
      fill="currentColor"
      class="text-celebrate"
      opacity="0.9"
    />
    {/* trend spark */}
    <path
      d="M40 60l14-8 12-6 12-12"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="text-celebrate"
      opacity="0.55"
    />
  </svg>
);

/** Checklist with a sparkle — empty habit list. */
export const ChecklistIllustration: Component<IllustrationProps> = (props) => (
  <svg {...baseProps(props)}>
    <circle cx="60" cy="60" r="52" class="text-accent" fill="currentColor" opacity="0.06" />
    <rect
      x="32"
      y="30"
      width="56"
      height="60"
      rx="10"
      stroke="currentColor"
      stroke-width="3"
      class="text-accent"
      opacity="0.85"
    />
    {/* rows */}
    <circle cx="46" cy="48" r="4" fill="currentColor" class="text-celebrate" />
    <line
      x1="56"
      y1="48"
      x2="78"
      y2="48"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.6"
    />
    <circle cx="46" cy="62" r="4" fill="currentColor" class="text-celebrate" opacity="0.6" />
    <line
      x1="56"
      y1="62"
      x2="74"
      y2="62"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.45"
    />
    <circle
      cx="46"
      cy="76"
      r="4"
      stroke="currentColor"
      stroke-width="2.5"
      class="text-accent"
      opacity="0.5"
    />
    <line
      x1="56"
      y1="76"
      x2="70"
      y2="76"
      stroke="currentColor"
      stroke-width="3"
      stroke-linecap="round"
      class="text-accent"
      opacity="0.3"
    />
    {/* sparkle */}
    <path
      d="M88 30l2.5 6 6 2.5-6 2.5-2.5 6-2.5-6-6-2.5 6-2.5 2.5-6Z"
      fill="currentColor"
      class="text-celebrate"
    />
  </svg>
);
